import { forwardRef, useLayoutEffect, useRef, type ReactNode } from 'react';
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'framer-motion';

const CanvasPage = forwardRef<HTMLDivElement, { children: ReactNode }>(({ children }, ref) => {
  const present = useIsPresent();
  const reducedMotion = useReducedMotion();
  const pageRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (pageRef.current) {
      pageRef.current.inert = !present;
    }
  }, [present]);

  return (
    <motion.div
      ref={(element) => {
        pageRef.current = element;
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      }}
      className="stage-canvas-page"
      data-exiting={!present || undefined}
      aria-hidden={!present || undefined}
      initial={{ opacity: reducedMotion ? 1 : 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.22, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
});

/** Surface identity, not a streaming revision, owns the transition. */
export function StageCanvasTransition({
  pageKey,
  children,
}: {
  pageKey: string;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <AnimatePresence initial={false} mode="popLayout">
        <CanvasPage key={pageKey}>{children}</CanvasPage>
      </AnimatePresence>
    </div>
  );
}
